import IsLoggedIn from '../api/login'
import { getContentData, getPublicContentData } from '../api/services'
import CONSTANTS from './constants'

const SPRINKLR_SCRIPT_ID = 'spr-live-chat-widget-init'
const SPRINKLR_POSITION_STYLE_ID = 'sprinklr-chat-position-overrides'
const CHATBOT_SCRIPT_CACHE_KEY = 'hesabiChatbotScript'
const SPRINKLR_CLASS_PATTERN = /ezg[a-z0-9]+/i
const SPRINKLR_IFRAME_HINT = /sprinklr|live-chat|spr-live|sprchat/i
const CONVERSATION_MIN_SIZE = 180

let positionObserver = null
let positionObserverFrame = null

export const SPRINKLR_CHAT_POSITION = {
  bottom: '24px',
  side: '24px',
  zIndex: 10050,
}

export const isSprinklrChatLoaded = () =>
  typeof window !== 'undefined' && window.sprChat?.loaded === true

export const isRtlLayout = () =>
  document.getElementById('dirLayout')?.getAttribute('dir') === 'rtl'

const getIframeHintText = (element) =>
  [
    element.getAttribute('src'),
    element.src,
    element.id,
    element.name,
    element.title,
    element.getAttribute('aria-label'),
  ]
    .filter(Boolean)
    .join(' ')

const isConversationSized = (element) => {
  const rect = element.getBoundingClientRect()
  return rect.width >= CONVERSATION_MIN_SIZE && rect.height >= CONVERSATION_MIN_SIZE
}

const isSprinklrIframe = (element) => {
  if (!element || element.tagName !== 'IFRAME') {
    return false
  }

  if (SPRINKLR_IFRAME_HINT.test(getIframeHintText(element))) {
    return true
  }

  const parentClass =
    typeof element.parentElement?.className === 'string'
      ? element.parentElement.className
      : ''
  if (SPRINKLR_CLASS_PATTERN.test(parentClass)) {
    return true
  }

  const sibling = element.previousElementSibling || element.nextElementSibling
  if (!sibling || sibling === element) {
    return false
  }

  const siblingClass =
    typeof sibling.className === 'string' ? sibling.className : ''
  return SPRINKLR_CLASS_PATTERN.test(siblingClass)
}

const isSprinklrRelatedElement = (element) => {
  if (!element || element.nodeType !== 1 || element.id === SPRINKLR_SCRIPT_ID) {
    return false
  }

  if (element.tagName === 'IFRAME') {
    return isSprinklrIframe(element)
  }

  const className =
    typeof element.className === 'string' ? element.className : ''
  if (SPRINKLR_CLASS_PATTERN.test(className)) {
    return true
  }

  return !!element.querySelector(
    'iframe[src*="sprinklr.com"], iframe[src*="live-chat.sprinklr.com"], [class*="ezg"]'
  )
}

const isFixedOrAbsolute = (element) => {
  const position = window.getComputedStyle(element).position
  return position === 'fixed' || position === 'absolute'
}

const SPRINKLR_NODE_SELECTOR =
  'iframe[src*="sprinklr.com"], iframe[src*="live-chat.sprinklr.com"], iframe[id*="spr"], iframe[title*="chat" i], [class*="ezg"]'

const isFixedPosition = (element) =>
  window.getComputedStyle(element).position === 'fixed'

export const findSprinklrPositionedElements = () => {
  const elements = new Set()

  const addIfPositionedHost = (element) => {
    if (!element || element === document.body) {
      return
    }
    if (isSprinklrRelatedElement(element) && isFixedPosition(element)) {
      elements.add(element)
    }
  }

  const addPositionedAncestors = (element) => {
    let current = element
    while (current && current !== document.body) {
      addIfPositionedHost(current)
      current = current.parentElement
    }
  }

  document.querySelectorAll(SPRINKLR_NODE_SELECTOR).forEach((element) => {
    addPositionedAncestors(element)
    addIfPositionedHost(element)
  })

  Array.from(document.body.children).forEach((child) => {
    if (child.id === SPRINKLR_SCRIPT_ID || child.tagName === 'SCRIPT') {
      return
    }

    if (isSprinklrRelatedElement(child) && isFixedOrAbsolute(child)) {
      if (isFixedPosition(child) || isConversationSized(child)) {
        elements.add(child)
      }
    }

    if (child.tagName === 'IFRAME' && isSprinklrIframe(child) && isFixedOrAbsolute(child)) {
      elements.add(child)
    }

    child.querySelectorAll(SPRINKLR_NODE_SELECTOR).forEach((nestedElement) => {
      addIfPositionedHost(nestedElement)
    })
  })

  document.querySelectorAll('body iframe').forEach((iframe) => {
    if (!isSprinklrIframe(iframe) || !isFixedOrAbsolute(iframe)) {
      return
    }
    if (isFixedPosition(iframe) || isConversationSized(iframe)) {
      elements.add(iframe)
    }
  })

  return [...elements]
}

export const findSprinklrLauncherElement = () =>
  document.querySelector('[class*="ezg1tqb0"]') ||
  findSprinklrPositionedElements()[0] ||
  null

export const extractScriptContent = (content) => {
  if (!content || typeof content !== 'string') {
    return null
  }

  const trimmed = content.trim()
  if (!trimmed.startsWith('<')) {
    return trimmed
  }

  const match = trimmed.match(/<script[^>]*>([\s\S]*?)<\/script>/i)
  return match ? match[1].trim() : null
}

export const publishSprinklrChatStatus = (status) => {
  if (typeof document === 'undefined') {
    return
  }

  document.body.classList.remove(
    'sprinklr-chat-loading',
    'sprinklr-chat-ready',
    'sprinklr-chat-failed',
    'sprinklr-chat-rtl',
    'sprinklr-chat-ltr'
  )
  document.body.classList.add(`sprinklr-chat-${status.state}`)
  document.body.classList.add(isRtlLayout() ? 'sprinklr-chat-rtl' : 'sprinklr-chat-ltr')

  window.dispatchEvent(
    new CustomEvent('sprinklrChatStatus', {
      detail: status,
    })
  )

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info('[Sprinklr Chat]', status)
  }
}

const applyPositionToElement = (element, sideProperty, oppositeSideProperty, position) => {
  element.classList.add('sprinklr-chat-position-target')
  const computed = window.getComputedStyle(element)
  const keepVertical = isConversationSized(element)
  const topValue = keepVertical ? computed.top : 'auto'
  const bottomValue = keepVertical ? computed.bottom : position.bottom
  const insetValue =
    sideProperty === 'left'
      ? `${topValue} auto ${bottomValue} ${position.side}`
      : `${topValue} ${position.side} ${bottomValue} auto`

  element.style.setProperty('inset', insetValue, 'important')
  element.style.setProperty(sideProperty, position.side, 'important')
  element.style.setProperty(oppositeSideProperty, 'auto', 'important')
  if (!keepVertical) {
    element.style.setProperty('bottom', position.bottom, 'important')
  }
  element.style.setProperty('z-index', String(position.zIndex), 'important')
}

export const applyChatPositionOverrides = (
  position = SPRINKLR_CHAT_POSITION
) => {
  const isRtl = isRtlLayout()

  document.body.classList.remove('sprinklr-chat-rtl', 'sprinklr-chat-ltr')
  document.body.classList.add(isRtl ? 'sprinklr-chat-rtl' : 'sprinklr-chat-ltr')

  let styleEl = document.getElementById(SPRINKLR_POSITION_STYLE_ID)
  if (!styleEl) {
    styleEl = document.createElement('style')
    styleEl.id = SPRINKLR_POSITION_STYLE_ID
    document.head.appendChild(styleEl)
  }

  const sideTargets = `
    body.sprinklr-chat-rtl > div[class*="ezg"],
    body.sprinklr-chat-ltr > div[class*="ezg"],
    body.sprinklr-chat-rtl .sprinklr-chat-position-target,
    body.sprinklr-chat-ltr .sprinklr-chat-position-target,
    body.sprinklr-chat-rtl iframe[src*="sprinklr.com"],
    body.sprinklr-chat-rtl iframe[src*="live-chat.sprinklr.com"],
    body.sprinklr-chat-ltr iframe[src*="sprinklr.com"],
    body.sprinklr-chat-ltr iframe[src*="live-chat.sprinklr.com"]
  `

  styleEl.textContent = `
    ${sideTargets} {
      z-index: ${position.zIndex} !important;
      right: ${position.side} !important;
      left: auto !important;
    }

    [class*="ezg1tqb0"] {
      bottom: ${position.bottom} !important;
    }
  `

  findSprinklrPositionedElements().forEach((element) => {
    applyPositionToElement(element, 'right', 'left', position)
  })
}

export const startSprinklrPositionObserver = () => {
  if (positionObserver || typeof MutationObserver === 'undefined') {
    return
  }

  positionObserver = new MutationObserver(() => {
    if (positionObserverFrame) {
      cancelAnimationFrame(positionObserverFrame)
    }

    positionObserverFrame = requestAnimationFrame(() => {
      applyChatPositionOverrides()
      positionObserverFrame = null
    })
  })

  positionObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'src', 'id', 'title'],
  })
}

export const stopSprinklrPositionObserver = () => {
  if (positionObserverFrame) {
    cancelAnimationFrame(positionObserverFrame)
    positionObserverFrame = null
  }

  if (!positionObserver) {
    return
  }

  positionObserver.disconnect()
  positionObserver = null
}

export const waitForSprinklrLauncher = (timeoutMs = 15000) =>
  new Promise((resolve) => {
    const launcher = findSprinklrLauncherElement()
    if (launcher || isSprinklrChatLoaded()) {
      resolve({
        ok: true,
        element: launcher || findSprinklrLauncherElement(),
      })
      return
    }

    const startedAt = Date.now()
    const timer = setInterval(() => {
      const foundLauncher = findSprinklrLauncherElement()
      if (foundLauncher || isSprinklrChatLoaded()) {
        clearInterval(timer)
        resolve({
          ok: true,
          element: foundLauncher || findSprinklrLauncherElement(),
        })
        return
      }

      if (Date.now() - startedAt > timeoutMs) {
        clearInterval(timer)
        resolve({ ok: false, reason: 'timeout' })
      }
    }, 500)
  })

const cacheChatbotScript = (script) => {
  try {
    sessionStorage.setItem(CHATBOT_SCRIPT_CACHE_KEY, script)
  } catch (e) {
    // Ignore storage quota issues.
  }
}

const getCachedChatbotScript = () => {
  try {
    return sessionStorage.getItem(CHATBOT_SCRIPT_CACHE_KEY)
  } catch (e) {
    return null
  }
}

export const fetchChatbotScript = async () => {
  const cachedScript = getCachedChatbotScript()
  if (cachedScript) {
    return {
      script: cachedScript,
      source: 'cache',
      error: null,
    }
  }

  const publicResponse = await getPublicContentData(CONSTANTS.IFRAME_KEY, true)
  const publicScript = publicResponse?.contentData?.[0]?.Chatbot
  if (publicScript) {
    cacheChatbotScript(publicScript)
    return {
      script: publicScript,
      source: 'public',
      error: null,
    }
  }

  if (IsLoggedIn()) {
    const authResponse = await getContentData(CONSTANTS.IFRAME_KEY, true)
    const authScript = authResponse?.contentData?.[0]?.Chatbot
    if (authScript) {
      cacheChatbotScript(authScript)
      return {
        script: authScript,
        source: 'authenticated',
        error: null,
      }
    }

    return {
      script: null,
      source: 'authenticated',
      error: authResponse?.error || publicResponse?.error,
    }
  }

  return {
    script: null,
    source: 'public',
    error: publicResponse?.error,
  }
}

export const loadSprinklrChatWidget = (scriptContent) => {
  if (isSprinklrChatLoaded()) {
    return true
  }

  const content = extractScriptContent(scriptContent)
  if (!content) {
    return false
  }

  if (document.getElementById(SPRINKLR_SCRIPT_ID)) {
    return true
  }

  const scriptEl = document.createElement('script')
  scriptEl.type = 'text/javascript'
  scriptEl.id = SPRINKLR_SCRIPT_ID
  scriptEl.text = content
  document.body.appendChild(scriptEl)
  return true
}
